import moment from 'moment-timezone';
import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { CellFunction, DateCell, RESPONSE_NONE } from '../../cells';
import { IEpxRefer } from '../../../../../libs/apps/epxrefer';

export const APPT_TYPES = {
  VASCULAR: 'Vascular Access',
  VASCULAR_SURGICAL: 'Vascular Access: Surgical Consult',
  VASCULAR_VESSEL: 'Vascular Access: Vessel Mapping',
  VASCULAR_ACCESS: 'Vascular Access: Access Placement',
  VASCULAR_SECOND: 'Vascular Access: 2nd of 2 Step Access Placement',
  VASCULAR_CANNULATE: 'Vascular Access: Ready to Cannulate',
  VASCULAR_CVC_REMOVAL: 'Vascular Access: CVC Removal',
  VASCULAR_INT_DYSFUNCTION:
    'Vascular Access Intervention: Signs & Symptoms of Dysfunction (AVF/G or CVC)',
  VASCULAR_INT_FAILURE: 'Vascular Access Intervention: Failure to Mature',
  IN_CLINIC: 'In-Clinic',
  LAB_DRAW: 'Lab Draw',
  LAB_DRAW_MED: 'Lab Draw (w/ Medication Review)',
  LAB_DRAW_HD: 'Lab Draw (Home Dialysis)',
  LAB_DRAW_KD: 'Lab Draw (w/ Kinetics & Data Card)',
  LAB_ADL: 'Adequacy, Pro-card, and Lab Draw (Dialysis)',
  DATA_CARD: 'Data Card (Dialysis)',
  KINETICS: 'Adequacy (Dialysis)',
  SHOT: 'Shot (Dialysis)',
  FLOWSHEET: 'Flowsheet (Dialysis)',
  EYE_EXAM: 'Eye Exam',
  COLONOSCOPY: 'Colonoscopy',
  DOCTOR_DAY: 'Doctor Day',
  LAB_APPT: 'Lab Appointment',
  INJECTION_APPT: 'Injection Appointment'
};

export const MISSED_REASON = {
  work: 'Work Conflict',
  child_care: 'Child Care',
  transportation: 'Transportation',
  cancel: 'Canceled',
  other: 'Other Reasons'
};

const ReferralCell: CellFunction<IEpxRefer> = ({
  row: { original: session }
}) => {
  let label = '?';
  if (session.type === 'reminder') {
    label = 'Appointment Reminder';
    if (session.meta && session.meta.appointmentType) {
      label = `${session.meta.appointmentType} ${label}`;
    }
  } else if (session.type === 'discharge') {
    label = 'Discharge Referral';
  } else if (session.type === 'diabetic_eye_exam') {
    label = 'Diabetic Eye Exam';
  } else if (session.type === 'colonoscopy') {
    label = 'Colonoscopy';
  }
  return <span>{label}</span>;
};

const StatusCell: CellFunction<IEpxRefer> = ({
  row: { original: session }
}) => {
  let label = '?';
  if (session.module === 'done') {
    label = 'Completed';
  } else if (session.module === 'reminder') {
    const now = moment();
    const appointmentAt = moment(session.appointmentAt);
    if (appointmentAt < now) {
      label = 'Completed';
    } else {
      label = 'Active';
    }
  } else {
    label = 'Active';
  }
  return <span>{label}</span>;
};

const NoteCell: CellFunction<IEpxRefer> = ({ row: { original: session } }) => {
  if (session.type === 'diabetic_eye_exam') {
    if (session.hadAppointment) {
      return <span>Had Exam</span>;
    } else if (session.hadAppointment === false) {
      return <span>Did Not Have Exam</span>;
    } else {
      return RESPONSE_NONE;
    }
  } else if (session.type === 'discharge' || session.type === 'reminder') {
    let reason = null;
    if (session.missedReason) {
      reason = MISSED_REASON[session.missedReason];
    }
    if (session.willMiss) {
      return <span>Will Miss {reason ? reason : null}</span>;
    } else if (session.attendedAppointment) {
      return <span>Attended</span>;
    } else if (session.attendedAppointment === false) {
      return <span>Missed {reason ? reason : null}</span>;
    } else {
      return RESPONSE_NONE;
    }
  }
  return RESPONSE_NONE;
};

const EpxReferTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxRefer>[] = [
    {
      Header: 'Referral',
      id: '_referral',
      Cell: ReferralCell
    },
    {
      Header: 'Appointment Date',
      id: 'appointmentAt',
      Cell: ResponseGuard(DateCell('appointmentAt') as CellFunction<IEpxRefer>)
    },
    {
      Header: 'Status',
      id: 'status',
      Cell: StatusCell
    },
    {
      Header: 'Note',
      id: 'note',
      Cell: ResponseGuard(NoteCell)
    }
  ];
  return (
    <SessionTable<IEpxRefer>
      url="/epxrefer/data/"
      columns={columns}
      {...props}
    />
  );
};

export default EpxReferTable;
